import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { useCategories } from '../../hooks/useCategories';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';
function getToken() { try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; } catch { return null; } }
function apiFetch(path, opts = {}) {
  return fetch(`${API_BASE}${path}`, { ...opts, headers: { 'Content-Type': 'application/json', 'x-worker-token': getToken(), ...opts.headers } })
    .then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)));
}
const cutSheetUrl = fn => `${API_BASE.replace('/api', '')}/uploads/${fn}`;
const fmtMoney = n => n == null || n === '' ? '—' : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const EMPTY = { description: '', unit: 'EACH', material_cost: '', labor_hours: '', category: '' };

export default function AdminMaterials() {
  const navigate = useNavigate();
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editId, setEditId] = useState(null); // 'new' | id | null
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [uploadingCut, setUploadingCut] = useState({});
  const [catFilter, setCatFilter] = useState('all');
  const { all: ALL_CATEGORIES } = useCategories();

  useEffect(() => { apiFetch('/parts').then(setParts).catch(() => {}).finally(() => setLoading(false)); }, []);

  function startAdd() { setEditId('new'); setForm(EMPTY); setErr(''); }
  function startEdit(p) { setEditId(p.id); setForm({ description: p.description || '', unit: p.unit || 'EACH', material_cost: p.material_cost ?? '', labor_hours: p.labor_hours ?? '', category: p.category || '' }); setErr(''); }
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function save() {
    if (!form.description.trim()) { setErr('Description is required'); return; }
    setSaving(true); setErr('');
    try {
      const isNew = editId === 'new';
      const saved = await apiFetch(isNew ? '/parts' : `/parts/${editId}`, { method: isNew ? 'POST' : 'PATCH', body: JSON.stringify(form) });
      setParts(prev => (isNew ? [...prev, saved] : prev.map(x => x.id === saved.id ? saved : x)));
      setEditId(null);
    } catch (e) { setErr(e.message || 'Save failed'); }
    setSaving(false);
  }
  async function del(id) {
    if (!window.confirm('Remove this part from the directory?')) return;
    try { await apiFetch(`/parts/${id}`, { method: 'DELETE' }); setParts(prev => prev.filter(x => x.id !== id)); } catch {}
  }
  async function uploadCut(partId, file) {
    if (!file) return;
    setUploadingCut(u => ({ ...u, [partId]: true }));
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await fetch(`${API_BASE}/parts/${partId}/cut-sheet`, { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd });
      if (!r.ok) throw new Error('Upload failed');
      const data = await r.json();
      setParts(prev => prev.map(p => p.id === partId ? { ...p, cut_sheet: data.filename } : p));
    } catch (e) { alert(e.message); }
    setUploadingCut(u => ({ ...u, [partId]: false }));
  }
  async function deleteCut(partId) {
    if (!window.confirm('Remove cut sheet for this part?')) return;
    try { await apiFetch(`/parts/${partId}/cut-sheet`, { method: 'DELETE' }); setParts(prev => prev.map(p => p.id === partId ? { ...p, cut_sheet: null } : p)); } catch {}
  }

  const inp = { border: '1px solid #d1d5db', borderRadius: 6, padding: '7px 9px', fontSize: 13, width: '100%', boxSizing: 'border-box' };

  // Group parts by category — canonical order first, then any stragglers, then Uncategorized.
  const catOf = p => (p.category && p.category.trim()) || 'Uncategorized';
  const q = search.trim().toLowerCase();
  const searched = parts.filter(p => !q || `${p.description || ''} ${p.category || ''}`.toLowerCase().includes(q));
  const extraCats = [...new Set(parts.map(catOf))].filter(c => !ALL_CATEGORIES.includes(c) && c !== 'Uncategorized');
  const orderedCats = [...ALL_CATEGORIES, ...extraCats, 'Uncategorized'];
  const countByCat = {}; searched.forEach(p => { const c = catOf(p); countByCat[c] = (countByCat[c] || 0) + 1; });
  const visible = catFilter === 'all' ? searched : searched.filter(p => catOf(p) === catFilter);
  const groups = orderedCats.map(c => ({ cat: c, items: visible.filter(p => catOf(p) === c) })).filter(g => g.items.length);
  const chip = on => ({ padding: '5px 12px', borderRadius: 20, border: 'none', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', background: on ? '#065f46' : '#f3f4f6', color: on ? '#fff' : '#374151', whiteSpace: 'nowrap' });

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div>
          <button onClick={() => navigate('/admin/settings')}
            style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 6 }}>‹ Settings</button>
          <h1 className="admin-page-title">Material Database</h1>
          <p className="admin-page-sub">{parts.length} part{parts.length !== 1 ? 's' : ''} · used across bids &amp; submittals</p>
        </div>
        <button className="admin-primary-btn" onClick={startAdd}>+ Add Part</button>
      </div>

      <div className="admin-card" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search description or category…" style={{ ...inp, maxWidth: 380 }} />
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
            <button onClick={() => setCatFilter('all')} style={chip(catFilter === 'all')}>All ({searched.length})</button>
            {orderedCats.filter(c => countByCat[c]).map(c => (
              <button key={c} onClick={() => setCatFilter(c)} style={chip(catFilter === c)}>{c} ({countByCat[c]})</button>
            ))}
          </div>
        </div>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
        ) : groups.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>{parts.length === 0 ? 'No parts yet — add your first.' : 'No matches.'}</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table">
              <thead>
                <tr><th>Description</th><th>Unit</th><th>Material Cost</th><th>Labor Hrs</th><th>Cut Sheet</th><th style={{ width: 70 }}></th></tr>
              </thead>
              <tbody>
                {groups.map(g => (
                  <React.Fragment key={g.cat}>
                    <tr>
                      <td colSpan={6} style={{ background: '#f0fdf4', fontWeight: 800, fontSize: 12, color: '#065f46', textTransform: 'uppercase', letterSpacing: 0.5, padding: '8px 16px' }}>
                        {g.cat} <span style={{ color: '#16a34a', fontWeight: 600 }}>· {g.items.length}</span>
                      </td>
                    </tr>
                    {g.items.map(p => (
                      <tr key={p.id}>
                        <td style={{ fontWeight: 600 }}>{p.description}</td>
                        <td className="admin-list-secondary">{p.unit || '—'}</td>
                        <td className="admin-list-secondary">{fmtMoney(p.material_cost)}</td>
                        <td className="admin-list-secondary">{p.labor_hours ?? '—'}</td>
                        <td>
                          {p.cut_sheet ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <a href={cutSheetUrl(p.cut_sheet)} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#1d4ed8', fontWeight: 600 }}>📄 View</a>
                              <button onClick={() => deleteCut(p.id)} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 13 }}>×</button>
                            </span>
                          ) : (
                            <label style={{ fontSize: 12, color: '#1d4ed8', fontWeight: 600, cursor: 'pointer' }}>
                              {uploadingCut[p.id] ? 'Uploading…' : '+ Upload'}
                              <input type="file" accept="application/pdf,image/*" style={{ display: 'none' }} onChange={e => { uploadCut(p.id, e.target.files[0]); e.target.value = ''; }} />
                            </label>
                          )}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button onClick={() => startEdit(p)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1d4ed8', fontSize: 12, fontWeight: 600 }}>Edit</button>
                          <button onClick={() => del(p.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 15, marginLeft: 4 }}>×</button>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setEditId(null)}>
          <div style={{ background: '#fff', borderRadius: 14, width: 520, maxWidth: '100%', boxShadow: '0 12px 48px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
            <div style={{ background: '#065f46', borderRadius: '14px 14px 0 0', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{editId === 'new' ? 'Add Part' : 'Edit Part'}</span>
              <button onClick={() => setEditId(null)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 3 }}>
                <label style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Description *</label>
                <input value={form.description} onChange={e => set('description', e.target.value)} style={inp} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <label style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Category</label>
                <select value={form.category} onChange={e => set('category', e.target.value)} style={{ ...inp, background: '#fff' }}>
                  <option value="">— Select category —</option>
                  {ALL_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  {form.category && !ALL_CATEGORIES.includes(form.category) && <option value={form.category}>{form.category}</option>}
                </select>
              </div>
              {[['Unit', 'unit', 'text'], ['Material Cost ($)', 'material_cost', 'number'], ['Labor Hours', 'labor_hours', 'number']].map(([label, key, type]) => (
                <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <label style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>{label}</label>
                  <input type={type} value={form[key]} onChange={e => set(key, e.target.value)} style={inp} />
                </div>
              ))}
              {err && <div style={{ gridColumn: '1 / -1', color: '#dc2626', fontSize: 13 }}>{err}</div>}
              <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4 }}>
                <button onClick={() => setEditId(null)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                <button onClick={save} disabled={saving} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: 'var(--orange)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{saving ? 'Saving…' : 'Save'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
